(() => {
  'use strict';

  class StickyATC {
    constructor(el) {
      this.el = el;
      this.sectionId = el.dataset.section;
      this.expanded = false;
      this.hasVariantSelected = false;

      this.bar = el.querySelector('.sticky-atc__bar');
      this.panel = el.querySelector('.sticky-atc__panel');
      this.expandBtn = el.querySelector('.sticky-atc__expand-btn');
      this.closeBtn = el.querySelector('.sticky-atc__close-btn');
      this.barAddBtn = el.querySelector('[data-sticky-bar-add]');
      this.formWrap = el.querySelector('.sticky-atc__submit-wrap');
      this.panelForm = el.querySelector(`#sticky-product-form-${this.sectionId}`);
      this.variantInput = el.querySelector(`#sticky-atc-variant-id-${this.sectionId}`);
      this.optionBtns = el.querySelectorAll('.sticky-atc__option-btn:not(.sticky-atc__upsell-btn)');
      this.upsellBtns = el.querySelectorAll('.sticky-atc__upsell-btn');
      this.hasUpsells = el.dataset.hasUpsells === 'true';
      this.panelSubmitBtn = el.querySelector('.sticky-atc__submit');

      const variantsEl = document.getElementById(`sticky-atc-variants-${this.sectionId}`);
      this.variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];

      this.selectedOptions = {};
      this._wantsVisible = false;

      // Scroll thresholds (vh) per breakpoint — 0 means no threshold
      this._thresholdDesktop = parseFloat(el.dataset.scrollDesktop) || 0;
      this._thresholdTablet  = parseFloat(el.dataset.scrollTablet)  || 0;
      this._thresholdMobile  = parseFloat(el.dataset.scrollMobile)  || 0;

      // Move to end of body after third-party widgets load (e.g. #shopify-chat)
      document.body.appendChild(this.el);
      setTimeout(() => document.body.appendChild(this.el), 1500);

      this._bindEvents();
      this._syncUpsellPills();
      this._observeMainATC();
      this._observeCartDrawer();
      this._lowerChatWidget();
      this._bindScrollThreshold();
      this._observeMainPrice();

      // Auto-apply for single default variant or when variant gating is disabled;
      // otherwise show the selection prompt
      const isSingleDefault = this.variants.length === 1 && this.variants[0].title === 'Default Title';
      const requireVariant = el.dataset.requireVariant !== 'false';
      if (isSingleDefault || !requireVariant) {
        const firstAvailable = this.variants.find(v => v.available) || this.variants[0];
        if (firstAvailable) this._applyVariant(firstAvailable);
      } else {
        this._showSelectPrompt();
      }
    }

    _bindEvents() {
      if (this.expandBtn) {
        this.expandBtn.addEventListener('click', () => this.togglePanel());
      }

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.collapsePanel());
      }

      this.optionBtns.forEach(btn => {
        btn.addEventListener('click', () => this._selectOption(btn));
      });

      this.upsellBtns.forEach(btn => {
        btn.addEventListener('click', () => this._toggleUpsell(btn));
      });

      // Keep the pills in step when upsells are toggled elsewhere on the page
      // (the need_extinguisher block or the kap_you-need section).
      document.addEventListener('change', e => {
        if (e.target && e.target.classList && e.target.classList.contains('upsell-checkbox')) {
          this._syncUpsellPills();
        }
      });

      if (this.barAddBtn) {
        this.barAddBtn.addEventListener('click', () => {
          // Open the panel first when there are options OR upsells to choose from,
          // so the shopper sees the extras before the item is added.
          if (this.expandBtn || this.hasUpsells) {
            this.expandPanel();
          } else {
            this._submitCart();
          }
        });
      }

      if (this.panelForm) {
        this.panelForm.addEventListener('submit', e => {
          e.preventDefault();
          this._submitCart();
        });
      }

      document.addEventListener('click', e => {
        if (this.expanded && !this.el.contains(e.target)) {
          this.collapsePanel();
        }
      });

      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && this.expanded) this.collapsePanel();
      });

      // Sync when main product variant picker changes (user explicitly picks on main form)
      const mainVariantSelects = document.getElementById(`variant-selects-${this.sectionId}`);
      if (mainVariantSelects) {
        mainVariantSelects.addEventListener('change', () => {
          requestAnimationFrame(() => this._syncFromMainForm());
        });
      }
    }

    _observeCartDrawer() {
      new MutationObserver(() => {
        if (document.body.classList.contains('overflow-hidden')) {
          this._hide();
        } else {
          this._show();
        }
      }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }

    _lowerChatWidget() {
      const apply = () => {
        const chat = document.getElementById('shopify-chat');
        if (chat) chat.style.zIndex = '9998';
      };
      apply();
      new MutationObserver(apply).observe(document.body, { childList: true, subtree: false });
    }

    _showSelectPrompt() {
      if (!this.formWrap || this.formWrap.querySelector('.sticky-atc__select-prompt')) return;
      const prompt = document.createElement('div');
      prompt.className = 'sticky-atc__select-prompt';
      prompt.textContent = 'Select options';
      this.formWrap.appendChild(prompt);
    }

    _hideSelectPrompt() {
      this.el.querySelectorAll('.sticky-atc__select-prompt').forEach(p => p.remove());
    }

    _observeMainATC() {
      const mainBtn = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      if (!mainBtn) return;

      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this._hide();
          } else {
            this._show();
          }
        });
      }, { threshold: 0 });

      observer.observe(mainBtn);
    }

    _syncFromMainForm() {
      const mainInput = document.querySelector(`#product-form-${this.sectionId} .product-variant-id`);
      if (!mainInput || !mainInput.value) return;
      const variantId = parseInt(mainInput.value);
      if (!variantId) return;
      const variant = this.variants.find(v => v.id === variantId);
      if (variant) this._applyVariant(variant);
    }

    _getScrollThresholdPx() {
      const w = window.innerWidth;
      const vh = w < 750 ? this._thresholdMobile
               : w < 990 ? this._thresholdTablet
               : this._thresholdDesktop;
      return (vh / 100) * window.innerHeight;
    }

    _bindScrollThreshold() {
      window.addEventListener('scroll', () => {
        if (!this._wantsVisible) return;
        if (window.scrollY >= this._getScrollThresholdPx()) {
          this.el.classList.add('is-visible');
          this.el.setAttribute('aria-hidden', 'false');
        } else {
          this.el.classList.remove('is-visible');
          this.el.setAttribute('aria-hidden', 'true');
        }
      }, { passive: true });
    }

    _show() {
      this._wantsVisible = true;
      if (window.scrollY >= this._getScrollThresholdPx()) {
        this.el.classList.add('is-visible');
        this.el.setAttribute('aria-hidden', 'false');
      }
    }

    _hide() {
      this._wantsVisible = false;
      this.el.classList.remove('is-visible');
      this.el.setAttribute('aria-hidden', 'true');
      this.collapsePanel();
    }

    togglePanel() {
      this.expanded ? this.collapsePanel() : this.expandPanel();
    }

    expandPanel() {
      this.expanded = true;
      this.el.classList.add('is-expanded');
      this.panel.removeAttribute('hidden');
      if (this.expandBtn) this.expandBtn.setAttribute('aria-expanded', 'true');
    }

    collapsePanel() {
      this.expanded = false;
      this.el.classList.remove('is-expanded');
      this.panel.setAttribute('hidden', '');
      if (this.expandBtn) this.expandBtn.setAttribute('aria-expanded', 'false');
    }

    _selectOption(btn) {
      if (btn.hasAttribute('data-unavailable')) return;

      const index = parseInt(btn.dataset.optionIndex);
      this.selectedOptions[index] = btn.dataset.value;

      // Update button states for this option group
      this.el.querySelectorAll(`.sticky-atc__option-btn[data-option-index="${index}"]`).forEach(b => {
        b.classList.toggle('is-selected', b === btn);
      });

      // Update displayed value
      const display = this.el.querySelector(`.sticky-atc__option-value[data-option-index="${index}"]`);
      if (display) display.textContent = btn.dataset.value;

      // Find and apply the matching variant
      const variant = this._findVariant();
      this._applyVariant(variant);

      // Also update the main product form so everything stays in sync
      this._syncToMainForm(variant);
    }

    // The buy-box price is repainted live by the need_extinguisher discount engine
    // (bundle total + upsell discounts) and by Dawn on variant change. The sticky bar
    // renders its own copy of the price snippet, which neither of those touch — so we
    // mirror the main .price element into the sticky's price slots whenever it changes.
    _mirrorPrice() {
      const container = document.getElementById(`price-${this.sectionId}`);
      const mainPrice = container ? container.querySelector('.price') : null;
      if (!mainPrice) return;
      const html = mainPrice.innerHTML;
      // Sync only the meaningful STATE classes — copying className wholesale would drag
      // the buy box's layout classes (price--large, price--show-badge) into the sticky.
      const onSale = mainPrice.classList.contains('price--on-sale');
      const soldOut = mainPrice.classList.contains('price--sold-out');
      this.el.querySelectorAll('.sticky-atc__price .price').forEach(p => {
        if (p.innerHTML !== html) p.innerHTML = html;
        p.classList.toggle('price--on-sale', onSale);
        p.classList.toggle('price--sold-out', soldOut);
      });
    }

    _observeMainPrice() {
      // Observe a stable ancestor: the price element itself is replaced wholesale on
      // variant change, which would detach an observer bound directly to it.
      const host = document.getElementById(`MainProduct-${this.sectionId}`);
      if (!host) return;
      this._mirrorPrice();
      let t;
      new MutationObserver(() => {
        clearTimeout(t);
        t = setTimeout(() => this._mirrorPrice(), 60);
      }).observe(host, { childList: true, subtree: true, characterData: true });
    }

    _toggleUpsell(btn) {
      if (btn.hasAttribute('data-unavailable')) return;
      const id = btn.dataset.upsellId;
      const selected = !btn.classList.contains('is-selected');
      btn.classList.toggle('is-selected', selected);
      btn.setAttribute('aria-pressed', selected ? 'true' : 'false');

      // Drive the page's real upsell checkboxes so the need_extinguisher block, the
      // live discount preview and the cart bundling all see the same selection.
      document.querySelectorAll('.upsell-checkbox').forEach(cb => {
        if (cb.value === id && cb.checked !== selected) {
          cb.checked = selected;
          cb.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    _syncUpsellPills() {
      if (!this.upsellBtns || !this.upsellBtns.length) return;
      const checked = {};
      document.querySelectorAll('.upsell-checkbox:checked').forEach(cb => {
        checked[cb.value] = true;
      });
      this.upsellBtns.forEach(btn => {
        const on = !!checked[btn.dataset.upsellId];
        btn.classList.toggle('is-selected', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    _findVariant() {
      return this.variants.find(v =>
        v.options.every((opt, i) => this.selectedOptions[i] === opt)
      ) || null;
    }

    _applyVariant(variant) {
      // Update selected options display to match variant
      if (variant) {
        variant.options.forEach((value, index) => {
          this.selectedOptions[index] = value;
          this.el.querySelectorAll(`.sticky-atc__option-btn[data-option-index="${index}"]`).forEach(btn => {
            btn.classList.toggle('is-selected', btn.dataset.value === value);
          });
          const display = this.el.querySelector(`.sticky-atc__option-value[data-option-index="${index}"]`);
          if (display) display.textContent = value;
        });
      }

      const available = variant && variant.available;

      if (this.variantInput && variant) {
        this.variantInput.value = variant.id;
      }

      // Show or hide the selection prompt
      if (variant) {
        this.hasVariantSelected = true;
        this._hideSelectPrompt();
      } else {
        this.hasVariantSelected = false;
        this._showSelectPrompt();
      }

      // Update panel submit button
      if (this.panelSubmitBtn) {
        this.panelSubmitBtn.disabled = !available;
        const span = this.panelSubmitBtn.querySelector('span') || this.panelSubmitBtn;
        span.textContent = !variant ? 'Select options' : (available ? 'Add to Cart' : 'Sold Out');
      }

      // Update bar add button
      if (this.barAddBtn) {
        this.barAddBtn.disabled = !available;
        const span = this.barAddBtn.querySelector('span') || this.barAddBtn;
        span.textContent = !variant ? 'Select options' : (available ? 'Add to Cart' : 'Sold Out');
      }
    }

    _syncToMainForm(variant) {
      if (!variant) return;

      // Update the hidden variant ID input only — do not dispatch change events on the
      // variant selects, as that triggers the theme's async fetch which updates the URL
      // and causes the page to scroll to the top.
      const mainInput = document.querySelector(`#product-form-${this.sectionId} .product-variant-id`);
      if (mainInput) mainInput.value = variant.id;
    }

    _getOptionName(index) {
      const btn = this.el.querySelector(`.sticky-atc__option-btn[data-option-index="${index}"]`);
      if (!btn) return '';
      const group = btn.closest('.sticky-atc__option-group');
      const legend = group ? group.querySelector('legend') : null;
      return legend ? legend.firstChild.textContent.trim().replace(/:$/, '') : '';
    }

    _submitCart() {
      const variantId = this.variantInput ? parseInt(this.variantInput.value) : null;
      if (!variantId) return;

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      const btns = [this.panelSubmitBtn, this.barAddBtn].filter(Boolean);
      btns.forEach(btn => {
        btn.disabled = true;
        const span = btn.querySelector('span') || btn;
        span.textContent = 'Adding…';
      });

      // Bundle any checked Kap upsells (need_extinguisher block) with the sticky add.
      // Mirrors the main add-to-cart contract: page-wide .upsell-checkbox:checked,
      // deduped by variant, tagged with a _parent property pointing at this product.
      const productId = this.el.dataset.productId;
      const upsellItems = [];
      const seen = {};
      seen[String(variantId)] = true;
      document.querySelectorAll('.upsell-checkbox:checked').forEach(cb => {
        const upId = parseInt(cb.value);
        if (!upId || seen[String(upId)]) return;
        seen[String(upId)] = true;
        const item = { id: upId, quantity: 1 };
        if (productId) item.properties = { _parent: String(productId) };
        upsellItems.push(item);
      });

      // Safety net: also honour pills selected in the sticky panel itself, in case the
      // page's upsell checkboxes aren't present (e.g. the block is hidden). Deduped above.
      this.el.querySelectorAll('.sticky-atc__upsell-btn.is-selected').forEach(btn => {
        const upId = parseInt(btn.dataset.upsellId);
        if (!upId || seen[String(upId)]) return;
        seen[String(upId)] = true;
        const item = { id: upId, quantity: 1 };
        if (productId) item.properties = { _parent: String(productId) };
        upsellItems.push(item);
      });

      // Shopify's /cart/add.js creates cart lines newest-first, so the posted array ends up
      // REVERSED in the cart — the LAST item posted sits at the TOP. Post the upsells reversed
      // and the primary last, so the cart reads: primary product first, then its upsells in the
      // order they were chosen. (Verified against the live cart.)
      const items = upsellItems.slice().reverse();
      items.push({ id: variantId, quantity: 1 });

      const body = { items: items };
      if (cart) {
        body.sections = cart.getSectionsToRender().map(s => s.id).join(',');
        body.sections_url = window.location.pathname;
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      })
        .then(res => {
          if (!res.ok) throw new Error();
          return res.json();
        })
        .then(parsedState => {
          btns.forEach(btn => {
            const span = btn.querySelector('span') || btn;
            span.textContent = 'Added!';
          });

          if (cart && parsedState.sections) {
            // The cart element keeps `is-empty` from page load when the cart started empty,
            // and CSS uses it to hide the drawer contents — so without this the drawer opens
            // blank even though the line items rendered fine. Dawn's own product-form does
            // the same before renderContents.
            if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
            const inner = cart.querySelector('.drawer__inner');
            if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');
            cart.renderContents(parsedState);
          }

          this._hide();

          setTimeout(() => {
            const variant = this.variants.find(v => v.id === variantId);
            this._applyVariant(variant);
          }, 1800);
        })
        .catch(() => {
          btns.forEach(btn => {
            btn.disabled = false;
            const span = btn.querySelector('span') || btn;
            span.textContent = 'Add to Cart';
          });
        });
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.sticky-atc').forEach(el => new StickyATC(el));
  });
})();
